import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Calendar as CalendarIcon, Download, ChevronLeft, ChevronRight, Users, UserCog, Camera, List, LayoutGrid, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { formatDate, cn } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useRealTimeRefresh } from '../context/RealTimeContext';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import html2canvas from 'html2canvas';

// Date Picker Modal Component
const DatePickerModal = ({ isOpen, onClose, currentDate, viewMode, onDateChange, onViewModeChange }) => {
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [selectedView, setSelectedView] = useState(viewMode);
  
  if (!isOpen) return null;

  const handleConfirm = () => {
    onDateChange(selectedDate);
    onViewModeChange(selectedView);
    onClose();
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    
    const days = [];
    // Empty cells for days before month starts
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    // Days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const changeMonth = (delta) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setSelectedDate(newDate);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 bg-zinc-900 border border-white/10 rounded-2xl shadow-2xl max-w-sm mx-auto overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">Sélectionner une date</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        {/* View Mode Selector */}
        <div className="p-4 border-b border-white/10">
          <p className="text-xs text-zinc-500 mb-2">Intervalle</p>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedView('weekly')}
              className={cn(
                "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                selectedView === 'weekly' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              )}
            >
              Semaine
            </button>
            <button
              onClick={() => setSelectedView('monthly')}
              className={cn(
                "flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                selectedView === 'monthly' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              )}
            >
              Mois
            </button>
          </div>
        </div>

        {/* Calendar */}
        <div className="p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronLeft size={20} className="text-zinc-400" />
            </button>
            <span className="text-white font-semibold">
              {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
            </span>
            <button 
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <ChevronRight size={20} className="text-zinc-400" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs text-zinc-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map((date, index) => (
              <button
                key={index}
                onClick={() => date && setSelectedDate(date)}
                disabled={!date}
                className={cn(
                  "aspect-square rounded-lg text-sm font-medium transition-colors",
                  !date && "invisible",
                  date && isSelected(date) && "bg-red-500 text-white",
                  date && isToday(date) && !isSelected(date) && "bg-white/10 text-red-400 ring-1 ring-red-500",
                  date && !isSelected(date) && !isToday(date) && "text-zinc-300 hover:bg-white/10"
                )}
              >
                {date?.getDate()}
              </button>
            ))}
          </div>

          {/* Today Button */}
          <button
            onClick={goToToday}
            className="w-full mt-4 py-2 text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            Aller à aujourd'hui
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 bg-zinc-800 border-white/10 text-white hover:bg-white/10"
          >
            Annuler
          </Button>
          <Button
            variant="brand"
            onClick={handleConfirm}
            className="flex-1"
          >
            Confirmer
          </Button>
        </div>
      </div>
    </>
  );
};

export default function Planning() {
  const [schedules, setSchedules] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly'
  const [staffType, setStaffType] = useState('chauffeurs'); // 'chauffeurs' or 'responsables'
  const [exporting, setExporting] = useState(false);
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const planningRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let startDate, endDate;
      
      if (viewMode === 'weekly') {
        startDate = getWeekStart(currentDate);
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
      } else {
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      }

      const [schedulesRes, employeesRes, responsablesRes] = await Promise.all([
        api.get('/schedules', {
          params: {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString()
          }
        }),
        api.get('/employees'),
        api.get('/employees/responsables')
      ]);
      
      setSchedules(schedulesRes.data);
      
      const responsableIds = responsablesRes.data.map(r => r.id);
      const chauffeurs = employeesRes.data.filter(e => !responsableIds.includes(e.id));
      
      setEmployees(chauffeurs);
      setResponsables(responsablesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement du planning');
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  // Fetch data on mount and when date/view changes
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time refresh when schedule is created
  useRealTimeRefresh(['schedule_created'], () => {
    fetchData();
  });

  // Auto-refresh every 1 second
  // Auto-refresh disabled - causes usability issues
  // useAutoRefresh(fetchData, 1000);

  const getWeekStart = (date) => {
    const start = new Date(date);
    const dayOfWeek = date.getDay();
    // Si dimanche (0), on va au lundi suivant (+1)
    // Sinon, on recule jusqu'au dimanche (- dayOfWeek)
    const diff = dayOfWeek === 0 ? 0 : -dayOfWeek;
    start.setDate(date.getDate() + diff);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  const getWeekDays = () => {
    const start = getWeekStart(currentDate);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const startDate = new Date(firstDay);
    startDate.setDate(firstDay.getDate() - firstDay.getDay());
    
    const endDate = new Date(lastDay);
    endDate.setDate(lastDay.getDate() + (6 - lastDay.getDay()));
    
    const days = [];
    const current = new Date(startDate);
    
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return days;
  };

  const getSchedulesForEmployeeAndDay = (employeeId, date) => {
    return schedules.filter(s => {
      const scheduleDate = new Date(s.date);
      return s.employee_id === employeeId && scheduleDate.toDateString() === date.toDateString();
    });
  };

  const getSchedulesForDay = (date) => {
    return schedules.filter(s => {
      const scheduleDate = new Date(s.date);
      return scheduleDate.toDateString() === date.toDateString();
    });
  };

  const getTotalForDay = (date) => {
    return getSchedulesForDay(date).filter(s => s.type === 'travail').length;
  };

  const getAstreinteForDay = (date) => {
    return getSchedulesForDay(date).filter(s => s.type === 'astreinte').length;
  };

  const previousPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'weekly') {
      newDate.setDate(currentDate.getDate() - 7);
    } else {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const nextPeriod = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'weekly') {
      newDate.setDate(currentDate.getDate() + 7);
    } else {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const captureScreenshot = async () => {
    if (!planningRef.current) return;
    
    setScreenshotMode(true);
    setExporting(true);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const canvas = await html2canvas(planningRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        windowHeight: planningRef.current.scrollHeight
      });
      
      const link = document.createElement('a');
      link.download = `planning_${staffType}_${formatDate(currentDate).replace(/\//g, '-')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Screenshot téléchargé ! Prêt pour WhatsApp');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      toast.error('Erreur lors de la capture');
    } finally {
      setExporting(false);
      setScreenshotMode(false);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      const startOfWeek = getWeekStart(currentDate);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);

      const response = await api.get('/schedules/export/csv', {
        params: {
          start_date: startOfWeek.toISOString(),
          end_date: endOfWeek.toISOString()
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `planning_${startOfWeek.toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Export CSV réussi !');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Erreur lors de l\'export CSV');
    } finally {
      setExporting(false);
    }
  };

  const formatDayHeader = (date) => {
    const days = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
    const day = days[date.getDay()];
    const dayNum = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}.\n${dayNum}/${month}`;
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  };

  const weekDays = getWeekDays();
  const monthDays = getMonthDays();
  const currentStaff = staffType === 'chauffeurs' ? employees : responsables;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-0 pb-24 md:pb-0" data-testid="planning-page">
      {/* Date Picker Modal - Mobile */}
      <DatePickerModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        currentDate={currentDate}
        viewMode={viewMode}
        onDateChange={setCurrentDate}
        onViewModeChange={setViewMode}
      />

      {/* Header Controls */}
      <div className="flex flex-col items-center gap-3">
        
        {/* Mobile Layout - Une seule ligne bien alignée */}
        <div className="flex md:hidden items-center justify-center gap-2 w-full px-2 mb-4">
          {/* Staff Type Selector - Même hauteur que les autres boutons */}
          <div className="flex items-center h-8 bg-zinc-800/50 rounded-xl border border-white/5">
            <button
              onClick={() => setStaffType('chauffeurs')}
              className={`h-8 px-2.5 rounded-xl text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                staffType === 'chauffeurs' 
                  ? 'bg-red-500 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="staff-chauffeurs-mobile"
            >
              <Users size={14} />
              <span>{employees.length}</span>
            </button>
            <button
              onClick={() => setStaffType('responsables')}
              className={`h-8 px-2.5 rounded-xl text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                staffType === 'responsables' 
                  ? 'bg-red-500 text-white' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="staff-responsables-mobile"
            >
              <UserCog size={14} />
              <span>{responsables.length}</span>
            </button>
          </div>

          {/* Date Picker Button - Même hauteur h-8 */}
          <button
            onClick={() => setShowDatePicker(true)}
            className="h-8 px-3 bg-zinc-800/50 border border-white/10 rounded-xl text-white hover:bg-white/10 flex items-center gap-2 text-xs font-medium"
            data-testid="mobile-date-picker"
          >
            <CalendarIcon size={14} className="text-red-400" />
            <span>{viewMode === 'weekly' ? 'Sem' : 'Mois'} {currentDate.getDate()}/{currentDate.getMonth() + 1}</span>
          </button>

          {/* Action Buttons - Tous h-8 */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={captureScreenshot}
              disabled={exporting}
              className="h-8 w-8 rounded-xl bg-green-500/20 border border-green-500/30 text-green-400 hover:bg-green-500/30 flex items-center justify-center"
              data-testid="screenshot-button-mobile"
            >
              <Camera size={16} />
            </button>
            <button
              onClick={exportToCSV}
              disabled={exporting}
              className="h-8 w-8 rounded-xl bg-zinc-800/50 border border-white/10 text-white hover:bg-white/10 flex items-center justify-center"
              data-testid="export-csv-button-mobile"
            >
              <Download size={16} />
            </button>
            {hasRole(['admin', 'responsable']) && (
              <button
                onClick={() => navigate('/planning/generator')}
                className="h-8 px-3 rounded-xl bg-red-500 text-white text-xs font-bold hover:bg-red-600 flex items-center justify-center"
                data-testid="go-to-generator-button-mobile"
              >
                GEN
              </button>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex flex-wrap items-center justify-center gap-2">
          {/* Staff Type Selector */}
          <div className="flex items-center gap-1 bg-zinc-800/50 backdrop-blur-sm rounded-xl p-1 border border-white/5">
            <button
              onClick={() => setStaffType('chauffeurs')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                staffType === 'chauffeurs' 
                  ? 'bg-red-500 text-white shadow-neon-red' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="staff-chauffeurs"
            >
              <Users size={14} />
              Chauffeurs ({employees.length})
            </button>
            <button
              onClick={() => setStaffType('responsables')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                staffType === 'responsables' 
                  ? 'bg-red-500 text-white shadow-neon-red' 
                  : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="staff-responsables"
            >
              <UserCog size={14} />
              Responsables ({responsables.length})
            </button>
          </div>

          {/* View Mode Selector */}
          <div className="flex items-center gap-1 bg-zinc-800/50 backdrop-blur-sm rounded-xl p-1 border border-white/5">
            <button
              onClick={() => setViewMode('weekly')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                viewMode === 'weekly' 
                  ? 'bg-white/10 text-white' 
                  : 'text-zinc-500 hover:text-white'
              }`}
              data-testid="view-weekly"
            >
              <List size={14} />
              Semaine
            </button>
            <button
              onClick={() => setViewMode('monthly')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                viewMode === 'monthly' 
                  ? 'bg-white/10 text-white' 
                  : 'text-zinc-500 hover:text-white'
              }`}
              data-testid="view-monthly"
            >
              <LayoutGrid size={14} />
              Mois
            </button>
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={previousPeriod} className="h-10 w-10 bg-zinc-800/50 border-white/10 text-white hover:bg-white/10">
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" onClick={goToToday} className="h-10 px-3 text-sm bg-zinc-800/50 border-white/10 text-white hover:bg-white/10">
              Aujourd'hui
            </Button>
            <Button variant="outline" size="icon" onClick={nextPeriod} className="h-10 w-10 bg-zinc-800/50 border-white/10 text-white hover:bg-white/10">
              <ChevronRight size={16} />
            </Button>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              onClick={captureScreenshot}
              variant="outline"
              disabled={exporting}
              className="h-10 px-3 bg-green-500/20 border-green-500/30 text-green-400 hover:bg-green-500/30"
              data-testid="screenshot-button"
            >
              <Camera size={16} className="mr-1" />
              <span className="text-xs">{exporting ? '...' : 'WA'}</span>
            </Button>
            <Button
              onClick={exportToCSV}
              variant="outline"
              disabled={exporting}
              className="h-10 px-3 bg-zinc-800/50 border-white/10 text-white hover:bg-white/10"
              data-testid="export-csv-button"
            >
              <Download size={16} className="mr-1" />
              <span className="text-xs">CSV</span>
            </Button>
            {hasRole(['admin', 'responsable']) && (
              <Button
                onClick={() => navigate('/planning/generator')}
                variant="brand"
                className="h-10 px-3 text-xs"
                data-testid="go-to-generator-button"
              >
                GEN
              </Button>
            )}
          </div>
        </div>

        {/* Période actuelle - Desktop only */}
        <div className="hidden md:flex items-center gap-2 text-white">
          <CalendarIcon size={16} className="text-red-400" />
          <span className="font-semibold text-sm">
            {viewMode === 'weekly' 
              ? `${formatDate(weekDays[0])} - ${formatDate(weekDays[6])}`
              : getMonthName(currentDate)
            }
          </span>
        </div>
      </div>

      {/* Weekly View - Excel Style */}
      {viewMode === 'weekly' && (
        <div 
          ref={planningRef}
          className="glass-panel overflow-hidden"
        >
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                {/* Header Row - Days */}
                <tr className="bg-zinc-800 text-white">
                  <th className="border border-zinc-700 p-3 text-left font-bold min-w-[180px]">
                    Nom du partenaire
                  </th>
                  {weekDays.map((day, index) => (
                    <th key={index} className="border border-zinc-700 p-2 text-center font-bold min-w-[60px] whitespace-pre-line">
                      {formatDayHeader(day)}
                    </th>
                  ))}
                </tr>
                {/* Total Row */}
                <tr className="bg-zinc-900/50">
                  <td className="border border-zinc-700 p-2 font-bold text-cyan-400">
                    Total prévu
                  </td>
                  {weekDays.map((day, index) => {
                    const total = getTotalForDay(day);
                    return (
                      <td key={index} className="border border-zinc-700 p-2 text-center font-bold text-cyan-400">
                        {total}
                      </td>
                    );
                  })}
                </tr>
                {/* Astreinte Row */}
                <tr className="bg-orange-500/10">
                  <td className="border border-zinc-700 p-2 font-bold text-orange-400">
                    Astreinte
                  </td>
                  {weekDays.map((day, index) => {
                    const astreinte = getAstreinteForDay(day);
                    return (
                      <td key={index} className="border border-zinc-700 p-2 text-center font-bold text-orange-400">
                        {astreinte > 0 ? astreinte : '-'}
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {currentStaff.length > 0 ? (
                  currentStaff.map((employee, empIndex) => (
                    <tr key={employee.id} className={empIndex % 2 === 0 ? 'bg-zinc-900/30' : 'bg-zinc-900/60'}>
                      <td className="border border-zinc-700 p-2 font-medium text-white">
                        {employee.prenom} {employee.nom}
                      </td>
                      {weekDays.map((day, dayIndex) => {
                        const daySchedules = getSchedulesForEmployeeAndDay(employee.id, day);
                        const schedule = daySchedules[0];
                        
                        let cellContent = '';
                        let cellClass = 'border border-zinc-700 p-2 text-center';
                        
                        if (schedule) {
                          if (schedule.type === 'travail') {
                            cellContent = 'TRAVAIL';
                            cellClass += ' bg-green-500 text-white font-bold';
                          } else if (schedule.type === 'astreinte') {
                            cellContent = 'ASTREINTE';
                            cellClass += ' bg-orange-500 text-white font-bold';
                          } else if (schedule.type === 'conge_paye') {
                            cellContent = 'CP';
                            cellClass += ' bg-blue-500 text-white font-bold';
                          } else if (schedule.type === 'absent') {
                            cellContent = 'ABSENT';
                            cellClass += ' bg-red-500 text-white font-bold';
                          } else if (schedule.type === 'arret_maladie') {
                            cellContent = 'MALADIE';
                            cellClass += ' bg-purple-500 text-white font-bold';
                          } else {
                            cellContent = schedule.type.toUpperCase();
                            cellClass += ' bg-zinc-600 text-white';
                          }
                        }
                        
                        return (
                          <td key={dayIndex} className={cellClass}>
                            {cellContent}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="border border-zinc-700 p-8 text-center text-zinc-500">
                      Aucun {staffType === 'chauffeurs' ? 'chauffeur' : 'responsable'} trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly View */}
      {viewMode === 'monthly' && (
        <div 
          ref={planningRef}
          className="glass-panel overflow-hidden"
        >
          {/* Month Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4">
            <h2 className="text-xl font-bold text-center uppercase tracking-wide">{getMonthName(currentDate)}</h2>
            <p className="text-center text-sm opacity-90">{currentStaff.length} {staffType}</p>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 border-b border-zinc-700">
            {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'].map((day, index) => (
              <div key={index} className="p-3 text-center font-semibold bg-zinc-800/50 border-r border-zinc-700 last:border-r-0 text-sm text-zinc-400">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7">
            {monthDays.map((day, index) => {
              const daySchedules = getSchedulesForDay(day);
              const workCount = daySchedules.filter(s => s.type === 'travail').length;
              const isToday = day.toDateString() === new Date().toDateString();
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              
              return (
                <div
                  key={index}
                  className={`min-h-[80px] p-2 border-r border-b border-zinc-700 ${
                    !isCurrentMonth ? 'bg-zinc-900/50 opacity-40' : 'bg-zinc-900/30'
                  } ${isToday ? 'bg-red-500/20' : ''}`}
                >
                  <div className="flex justify-between items-start">
                    <span className={`inline-block w-6 h-6 leading-6 text-center rounded-full text-sm ${
                      isToday ? 'bg-red-500 text-white' : 'text-zinc-400'
                    }`}>
                      {day.getDate()}
                    </span>
                    {workCount > 0 && (
                      <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded font-bold shadow-[0_0_10px_rgba(0,230,118,0.4)]">
                        {workCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="p-4 bg-zinc-900/50 border-t border-zinc-700">
            <div className="flex flex-wrap gap-4 justify-center text-xs text-zinc-400">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Nombre de chauffeurs en travail</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend for weekly view */}
      {viewMode === 'weekly' && (
        <div className="glass-panel p-4 mt-6">
          <div className="flex flex-wrap gap-4 justify-center text-xs">
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-green-500 rounded"></div>
              <span className="text-zinc-400">TRAVAIL</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-orange-500 rounded"></div>
              <span className="text-zinc-400">ASTREINTE</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-blue-500 rounded"></div>
              <span className="text-zinc-400">CONGÉ PAYÉ</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-red-500 rounded"></div>
              <span className="text-zinc-400">ABSENT</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-6 h-4 bg-purple-500 rounded"></div>
              <span className="text-zinc-400">MALADIE</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
